import { useEffect, useRef, useState } from 'react';
import { CPT_CODES } from '../../constants/CptCodes';

const TEAL = '#1DB8A0';
const TEAL_DARK = '#0F9E88';
const TEAL_LIGHT = '#E0F7F4';

const STATUS_CONFIG = {
  billable: {
    dot: '#22C55E',
    badge: { bg: '#DCFCE7', color: '#166534', label: 'Billable' },
  },
  partial: {
    dot: '#F59E0B',
    badge: { bg: '#FEF3C7', color: '#92400E', label: 'Partial' },
  },
  not_supported: {
    dot: '#EF4444',
    badge: { bg: '#FEE2E2', color: '#991B1B', label: 'Not supported' },
  },
};

const DEFAULT_PAYER = {
  payer_key: 'mc',
  payer_name: 'Medicare',
  is_default: true,
};

const isMedicarePayer = (payer) =>
  String(payer?.payer_name || '').trim().toLowerCase() === 'medicare' ||
  ['mc', 'medicare'].includes(
    String(payer?.payer_key || '').trim().toLowerCase(),
  );

const Coding = ({
  conversationID,
  apiClient,
  initialResults,
  initialPayerName,
  noteType,
  autoRun,
  getCurrentNote,
}) => {
  const [ruleSets, setRuleSets] = useState([]);
  const [activePayer, setActivePayer] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [extendedResults, setExtendedResults] = useState([]);
  const [loading, setLoading] = useState(false);
  // Why the last fetch came back with no codes: 'error' | 'none_found'
  const [emptyReason, setEmptyReason] = useState('');
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState({});
  const [rationales, setRationales] = useState({});
  const [copied, setCopied] = useState({});
  const [clinicNotes, setClinicNotes] = useState('');
  const [clinicNotesOpen, setClinicNotesOpen] = useState(false);
  const [extendedOpen, setExtendedOpen] = useState(false);
  const [extendedExpanded, setExtendedExpanded] = useState({});
  const dropdownRef = useRef(null);
  const recommendationInFlightRef = useRef(false);
  const lastAutoRunRef = useRef(null);

  // Restore the saved result's payer; new analyses start with Medicare.
  useEffect(() => {
    if (!apiClient) return;
    let cancelled = false;
    apiClient
      .getBillingRules()
      .then((rsp) => {
        if (cancelled) return;
        const sets = rsp?.payers || [];
        setRuleSets(sets);
        setActivePayer(
          initialPayerName
            ? sets.find((payer) => payer.payer_name === initialPayerName) ||
                { payer_name: initialPayerName }
            : sets.find(isMedicarePayer) || DEFAULT_PAYER,
        );
      })
      .catch(() => {
        if (!cancelled) {
          setActivePayer(initialPayerName ? { payer_name: initialPayerName } : DEFAULT_PAYER);
        }
      });
    return () => { cancelled = true; };
  }, [apiClient, initialPayerName]);

  // Load cached results from initialResults
  useEffect(() => {
    setResults(Array.isArray(initialResults) ? initialResults : []);
  }, [initialResults]);

  // The tab mounts before it is opened, so the parent signals each completed
  // Comprehend run. Wait for any active coding request before using the latest.
  useEffect(() => {
    if (
      !autoRun ||
      autoRun.conversationID !== conversationID ||
      lastAutoRunRef.current === autoRun ||
      !activePayer ||
      loading
    )
      return;
    lastAutoRunRef.current = autoRun;
    const medicarePayer = ruleSets.find(isMedicarePayer) || DEFAULT_PAYER;
    setActivePayer(medicarePayer);
    fetchRecommendations(medicarePayer.payer_key, autoRun.session);
  }, [autoRun, activePayer, conversationID, loading, ruleSets]);

  const applyResults = (rsp) => {
    const recs = rsp?.recommendations || [];
    setResults(recs);
    setExtendedResults(rsp?.extended_recommendations || []);
    setClinicNotes(rsp?.clinic_notes || '');
    if (recs.length > 0) setEmptyReason('');
    else if (rsp?.error) setEmptyReason('error');
    else setEmptyReason('none_found');
    const initRationales = {};
    recs.forEach((r, i) => {
      if (r.emr_note || r.rationale)
        initRationales[i] = r.emr_note || r.rationale;
    });
    setRationales(initRationales);
  };

  const fetchRecommendations = async (payerKey, session = null) => {
    if (
      !conversationID ||
      recommendationInFlightRef.current ||
      session?.isDisposed?.()
    )
      return;
    recommendationInFlightRef.current = true;
    setLoading(true);
    setDropdownOpen(false);
    setExpanded({});
    setExtendedExpanded({});
    setExtendedOpen(false);

    let existingValues;
    if (getCurrentNote) {
      try {
        existingValues = await getCurrentNote(session);
        if (session?.isDisposed?.()) {
          recommendationInFlightRef.current = false;
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error(error);
        setResults([]);
        setEmptyReason('error');
        recommendationInFlightRef.current = false;
        setLoading(false);
        return;
      }
    }

    try {
      const rsp = await apiClient.getCodingRecommendations(conversationID, {
        payer_key: payerKey,
        note_type: noteType || null,
        // cat lets the backend pick a profession-relevant, category-diverse
        // subset instead of slicing the category-ordered catalog top-down
        extended_codes: CPT_CODES.map(({ code, desc, cat }) => ({
          code,
          desc,
          cat,
        })),
        existing_values: existingValues,
      });
      applyResults(rsp);
      recommendationInFlightRef.current = false;
      setLoading(false);
      return;
    } catch (e) {
      // Request failed (e.g. timeout) — fall through to polling
      console.error(e);
    }

    // Poll status every 2s until complete or error
    const POLL_INTERVAL = 2000;
    const MAX_POLLS = 60; // ~2 minutes
    let polls = 0;

    const poll = async () => {
      polls++;
      try {
        const rsp =
          await apiClient.getCodingRecommendationsStatus(conversationID);
        if (rsp?.status === 'complete') {
          applyResults(rsp);
          recommendationInFlightRef.current = false;
          setLoading(false);
          return;
        }
        if (rsp?.status === 'error' || polls >= MAX_POLLS) {
          setResults([]);
          setEmptyReason('error');
          recommendationInFlightRef.current = false;
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error(err);
        setResults([]);
        setEmptyReason('error');
        recommendationInFlightRef.current = false;
        setLoading(false);
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };

    setTimeout(poll, POLL_INTERVAL);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectPayer = (rs) => {
    setActivePayer(rs);
    setDropdownOpen(false);
    fetchRecommendations(rs.payer_key);
  };

  const toggleCard = (i) => setExpanded((p) => ({ ...p, [i]: !p[i] }));

  const copyRationale = async (i) => {
    const text = rationales[i] || results[i]?.rationale || '';
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
    setCopied((p) => ({ ...p, [i]: true }));
    setTimeout(() => setCopied((p) => ({ ...p, [i]: false })), 2000);
  };

  const structuredResults =
    Array.isArray(results) &&
    results.length > 0 &&
    typeof results[0] === 'object';
  const messageOnly =
    Array.isArray(results) &&
    results.length > 0 &&
    typeof results[0] === 'string';

  const greenCount = structuredResults
    ? results.filter((r) => r.status === 'billable').length
    : 0;
  const yellowCount = structuredResults
    ? results.filter((r) => r.status === 'partial').length
    : 0;
  const redCount = structuredResults
    ? results.filter((r) => r.status === 'not_supported').length
    : 0;

  const configuredMedicare = ruleSets.find(isMedicarePayer);
  const payerList = configuredMedicare
    ? ruleSets
    : [DEFAULT_PAYER, ...ruleSets];
  const payerLabel = activePayer?.payer_name || 'Medicare';
  const payerInitials = payerLabel
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const emptyTitle =
    emptyReason === 'error'
      ? 'Coding recommendations unavailable'
      : 'No billable codes found';
  const emptyDetail =
    emptyReason === 'error'
      ? 'Something went wrong while analyzing this visit.'
      : 'Nothing in the note or transcript supports a CPT code yet.';

  return (
    <div
      style={{
        padding: '14px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      {/* Clinic Notes — collapsible, shown when clinic has notes for the active payer */}
      {clinicNotes && (
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => setClinicNotesOpen((o) => !o)}
            style={{
              width: '100%',
              background: '#F3F4F6',
              border: '1px solid #E5E7EB',
              borderRadius: clinicNotesOpen ? '8px 8px 0 0' : 8,
              padding: '9px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 11, marginRight: 2 }}>🏥</span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                color: '#6B7280',
                flex: 1,
              }}
            >
              Clinic Notes
            </span>
            <span
              style={{
                fontSize: 18,
                color: '#9CA3AF',
                transition: 'transform 0.15s',
                transform: clinicNotesOpen ? 'rotate(180deg)' : 'none',
                display: 'inline-block',
              }}
            >
              ▾
            </span>
          </button>
          {clinicNotesOpen && (
            <div
              style={{
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                padding: '10px 12px',
              }}
            >
              {clinicNotes
                .split('\n')
                .filter((line) => line.trim())
                .map((line, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 6,
                      fontSize: 10,
                      color: '#374151',
                      lineHeight: 1.6,
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{ color: '#9CA3AF', flexShrink: 0, marginTop: 1 }}
                    >
                      •
                    </span>
                    {line.trim()}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Medicare is selected first; other configured payers remain available. */}
      <>
        <div
          style={{
            fontSize: '10px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: '#9CA3AF',
            marginBottom: '6px',
          }}
        >
          Payer
        </div>
        <div
          style={{ position: 'relative', marginBottom: '16px' }}
          ref={dropdownRef}
        >
          <button
            disabled={loading}
            onClick={() => setDropdownOpen((o) => !o)}
            style={{
              width: '100%',
              background: '#fff',
              border: `1.5px solid ${dropdownOpen ? TEAL : '#E5E7EB'}`,
              borderRadius: dropdownOpen ? '10px 10px 0 0' : '10px',
              padding: '10px 36px 10px 12px',
              fontSize: '13px',
              fontWeight: 500,
              color: '#111827',
              cursor: loading ? 'default' : 'pointer',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              position: 'relative',
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                background: '#DBEAFE',
                color: '#1E3A8A',
                fontSize: 10,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {payerInitials}
            </div>
            <span style={{ flex: 1 }}>{payerLabel}</span>
            {isMedicarePayer(activePayer) && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  background: TEAL_LIGHT,
                  color: TEAL_DARK,
                  borderRadius: 4,
                  padding: '1px 6px',
                }}
              >
                Default
              </span>
            )}
            <span
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: `translateY(-50%) rotate(${dropdownOpen ? 180 : 0}deg)`,
                transition: 'transform 0.15s',
                fontSize: 18,
                color: '#9CA3AF',
                pointerEvents: 'none',
              }}
            >
              ▾
            </span>
          </button>

          {dropdownOpen && (
            <div
              style={{
                border: `1.5px solid ${TEAL}`,
                borderTop: 'none',
                borderRadius: '0 0 10px 10px',
                background: '#fff',
                overflow: 'hidden',
              }}
            >
              {payerList.map((rs, i) => {
                const initials = (rs.payer_name || rs.payer_key || '?')
                  .split(' ')
                  .map((w) => w[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase();
                const isSelected = rs.payer_key === activePayer?.payer_key;
                return (
                  <div
                    key={rs.payer_key || i}
                    onClick={() => selectPayer(rs)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      padding: '10px 12px',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 500,
                      color: isSelected ? TEAL_DARK : '#374151',
                      background: isSelected ? '#F0FDF9' : '#fff',
                      borderTop: i > 0 ? '1px solid #E5E7EB' : 'none',
                    }}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        background: '#DBEAFE',
                        color: '#1E3A8A',
                        fontSize: 10,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {initials}
                    </div>
                    <span style={{ flex: 1 }}>{rs.payer_name}</span>
                    {isMedicarePayer(rs) && (
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          background: TEAL_LIGHT,
                          color: TEAL_DARK,
                          borderRadius: 4,
                          padding: '1px 6px',
                        }}
                      >
                        Default
                      </span>
                    )}
                    {isSelected && (
                      <span style={{ fontSize: 11, color: TEAL }}>✓</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </>

      {/* Loading state */}
      {loading && (
        <div
          style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF' }}
        >
          <div style={{ fontSize: 13 }}>Analyzing note and transcript...</div>
        </div>
      )}

      {/* Message-only response (short transcript, etc.) */}
      {!loading && messageOnly && (
        <div style={{ fontSize: 13, color: '#6B7280', padding: '8px 0' }}>
          {results[0]}
        </div>
      )}

      {/* Empty — the fetch finished without any codes */}
      {!loading && !messageOnly && !structuredResults && emptyReason && (
        <div style={{ textAlign: 'center', padding: '40px 16px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
            {emptyTitle}
          </div>
          <div
            style={{
              fontSize: 12,
              color: '#9CA3AF',
              lineHeight: 1.5,
              marginTop: 4,
              maxWidth: 260,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            {emptyDetail}
          </div>
        </div>
      )}

      {/* Results */}
      {!loading && structuredResults && (
        <>
          {/* Summary chips */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {[
              {
                count: greenCount,
                label: 'Billable',
                bg: '#DCFCE7',
                val: '#166534',
                lbl: '#16a34a',
              },
              {
                count: yellowCount,
                label: 'Partial',
                bg: '#FEF3C7',
                val: '#92400E',
                lbl: '#B45309',
              },
              {
                count: redCount,
                label: 'Not supported',
                bg: '#FEE2E2',
                val: '#991B1B',
                lbl: '#DC2626',
              },
            ].map(({ count, label, bg, val, lbl }) => (
              <div
                key={label}
                style={{
                  flex: 1,
                  borderRadius: 10,
                  padding: '9px 6px',
                  textAlign: 'center',
                  background: bg,
                }}
              >
                <div style={{ fontSize: 20, fontWeight: 700, color: val }}>
                  {count}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: lbl,
                    marginTop: 1,
                  }}
                >
                  {label}
                </div>
              </div>
            ))}
          </div>

          {/* CPT cards — primary configured codes */}
          {results.map((rec, i) => {
            const status = rec.status || 'not_supported';
            const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_supported;
            const isOpen = !!expanded[i];
            const isEditing = !!editing[i];

            return (
              <div
                key={i}
                style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: 12,
                  marginBottom: 8,
                  overflow: 'hidden',
                  background: '#fff',
                }}
              >
                {/* Card header */}
                <div
                  onClick={() => toggleCard(i)}
                  style={{ padding: '11px 12px', cursor: 'pointer' }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                  >
                    <div
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        background: cfg.dot,
                        flexShrink: 0,
                      }}
                    />
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#111827',
                      }}
                    >
                      CPT: {rec.code}
                      {rec.description ? ` — ${rec.description}` : ''}
                    </div>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '3px 9px',
                        borderRadius: 20,
                        background: cfg.badge.bg,
                        color: cfg.badge.color,
                        flexShrink: 0,
                      }}
                    >
                      {cfg.badge.label}
                    </span>
                    <span
                      style={{
                        fontSize: 18,
                        color: '#9CA3AF',
                        transition: 'transform 0.18s',
                        transform: isOpen ? 'rotate(180deg)' : 'none',
                      }}
                    >
                      ▾
                    </span>
                  </div>
                </div>

                {/* Card body */}
                {isOpen && (
                  <div
                    style={{
                      borderTop: '1px solid #E5E7EB',
                      padding: 12,
                      background: '#F9FAFB',
                    }}
                  >
                    {/* BILLABLE: why billable explanation + EMR paste block */}
                    {status === 'billable' && (
                      <>
                        {rec.rationale && (
                          <div
                            style={{
                              background: '#fff',
                              border: '1px solid #E5E7EB',
                              borderRadius: 8,
                              padding: '10px 12px',
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.07em',
                                color: '#9CA3AF',
                                marginBottom: 6,
                              }}
                            >
                              <span style={{ color: '#22C55E' }}>✓ </span> Why
                              you can bill this
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                color: '#374151',
                                lineHeight: 1.55,
                              }}
                            >
                              {rec.rationale}
                            </div>
                          </div>
                        )}
                        {(rec.emr_note || rec.rationale) && (
                          <>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                margin: '10px 0',
                              }}
                            >
                              <div
                                style={{
                                  flex: 1,
                                  height: 1,
                                  background: '#E5E7EB',
                                }}
                              />
                              <div
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.07em',
                                  color: '#22C55E',
                                }}
                              >
                                Billing Justification
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  height: 1,
                                  background: '#E5E7EB',
                                }}
                              />
                            </div>
                            <div
                              style={{
                                background: '#F0FDF4',
                                border: '1px solid #BBF7D0',
                                borderRadius: 10,
                                padding: '11px 12px',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  marginBottom: 8,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.07em',
                                    color: '#166534',
                                  }}
                                >
                                  Paste into EMR
                                </div>
                                <div
                                  style={{
                                    display: 'flex',
                                    gap: 6,
                                    alignItems: 'center',
                                  }}
                                >
                                  <button
                                    onClick={() =>
                                      setEditing((p) => ({ ...p, [i]: !p[i] }))
                                    }
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 600,
                                      padding: '3px 9px',
                                      borderRadius: 20,
                                      cursor: 'pointer',
                                      border: '1px solid #BBF7D0',
                                      background: '#fff',
                                      color: '#166534',
                                    }}
                                  >
                                    {isEditing ? '✓ Done' : '✎ Edit'}
                                  </button>
                                  <button
                                    onClick={() => copyRationale(i)}
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 600,
                                      padding: '3px 9px',
                                      borderRadius: 20,
                                      cursor: 'pointer',
                                      border: '1px solid #166534',
                                      background: '#166534',
                                      color: '#fff',
                                    }}
                                  >
                                    {copied[i] ? 'Copied!' : 'Copy'}
                                  </button>
                                </div>
                              </div>
                              <div
                                contentEditable={isEditing}
                                suppressContentEditableWarning
                                onInput={(e) =>
                                  setRationales((p) => ({
                                    ...p,
                                    [i]: e.currentTarget.innerText,
                                  }))
                                }
                                style={{
                                  fontSize: 12,
                                  color: '#166534',
                                  lineHeight: 1.65,
                                  fontStyle: 'italic',
                                  outline: 'none',
                                  borderBottom: isEditing
                                    ? '1.5px dashed #86EFAC'
                                    : 'none',
                                }}
                              >
                                {rationales[i] ??
                                  (rec.emr_note || rec.rationale)}
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {/* PARTIAL: what's missing + suggestion */}
                    {status === 'partial' && (
                      <>
                        {rec.missing && (
                          <div
                            style={{
                              background: '#fff',
                              border: '1px solid #E5E7EB',
                              borderRadius: 8,
                              padding: '10px 12px',
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.07em',
                                color: '#9CA3AF',
                                marginBottom: 8,
                              }}
                            >
                              <span style={{ color: '#F59E0B' }}>⚠ </span>{' '}
                              What's missing
                            </div>
                            {rec.missing.split(',').map(
                              (item, mi) =>
                                item.trim() && (
                                  <div
                                    key={mi}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: 7,
                                      fontSize: 11,
                                      color: '#374151',
                                      lineHeight: 1.5,
                                      marginBottom: 5,
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: 5,
                                        height: 5,
                                        borderRadius: '50%',
                                        background: '#F59E0B',
                                        flexShrink: 0,
                                        marginTop: 5,
                                        display: 'inline-block',
                                      }}
                                    />
                                    {item.trim()}
                                  </div>
                                ),
                            )}
                          </div>
                        )}
                        {rec.suggestion && (
                          <>
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.07em',
                                color: '#9CA3AF',
                                marginBottom: 6,
                              }}
                            >
                              Suggested addition
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: '#374151',
                                lineHeight: 1.6,
                                padding: '9px 11px',
                                background: TEAL_LIGHT,
                                borderRadius: 8,
                                borderLeft: `3px solid ${TEAL}`,
                              }}
                            >
                              {rec.suggestion}
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {/* NOT SUPPORTED: rationale + suggestion */}
                    {status === 'not_supported' && (
                      <>
                        {rec.rationale && (
                          <div
                            style={{
                              background: '#fff',
                              border: '1px solid #E5E7EB',
                              borderRadius: 8,
                              padding: '10px 12px',
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.07em',
                                color: '#9CA3AF',
                                marginBottom: 6,
                              }}
                            >
                              <span style={{ color: '#EF4444' }}>✕ </span> Why
                              this can't be billed
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                color: '#374151',
                                lineHeight: 1.55,
                              }}
                            >
                              {rec.rationale}
                            </div>
                          </div>
                        )}
                        {rec.suggestion && (
                          <>
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.07em',
                                color: '#9CA3AF',
                                marginBottom: 6,
                              }}
                            >
                              What would make it billable
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: '#374151',
                                lineHeight: 1.6,
                                padding: '9px 11px',
                                background: TEAL_LIGHT,
                                borderRadius: 8,
                                borderLeft: `3px solid ${TEAL}`,
                              }}
                            >
                              {rec.suggestion}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Additional catalog codes evaluated alongside configured codes */}
          {extendedResults.length > 0 && (
            <div
              style={{
                border: '1px solid #E5E7EB',
                borderRadius: 12,
                marginTop: 4,
                overflow: 'hidden',
                background: '#fff',
              }}
            >
              {/* Trigger row */}
              <div
                onClick={() => setExtendedOpen((o) => !o)}
                style={{
                  padding: '11px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: '#F0FDF4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                >
                  📋
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: TEAL,
                    }}
                  >
                    View additional codes
                  </div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>
                    {extendedResults.length} more code
                    {extendedResults.length !== 1 ? 's' : ''} evaluated
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 18,
                    color: '#9CA3AF',
                    transition: 'transform 0.15s',
                    transform: extendedOpen ? 'rotate(180deg)' : 'none',
                    display: 'inline-block',
                  }}
                >
                  ▾
                </span>
              </div>

              {/* Expanded list */}
              {extendedOpen && (
                <div
                  style={{
                    borderTop: '1px solid #E5E7EB',
                    background: '#F9FAFB',
                    padding: '8px 12px',
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: '#6B7280',
                      marginBottom: 8,
                      lineHeight: 1.5,
                    }}
                  >
                    Additional codes evaluated against this visit. Tap any to
                    see details.
                  </div>
                  {extendedResults.map((rec, i) => {
                    const status = rec.status || 'partial';
                    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.partial;
                    const isOpen = !!extendedExpanded[i];
                    return (
                      <div
                        key={i}
                        style={{
                          border: '1px solid #E5E7EB',
                          borderRadius: 10,
                          marginBottom: 6,
                          overflow: 'hidden',
                          background: '#fff',
                        }}
                      >
                        <div
                          onClick={() =>
                            setExtendedExpanded((p) => ({
                              ...p,
                              [i]: !p[i],
                            }))
                          }
                          style={{
                            padding: '9px 12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <div
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: cfg.dot,
                              flexShrink: 0,
                            }}
                          />
                          <div
                            style={{
                              flex: 1,
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#111827',
                            }}
                          >
                            {rec.code}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: '#6B7280',
                              flex: 2,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {rec.description}
                          </div>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: 20,
                              background: cfg.badge.bg,
                              color: cfg.badge.color,
                              flexShrink: 0,
                            }}
                          >
                            {cfg.badge.label}
                          </span>
                        </div>
                        {isOpen && (
                          <div
                            style={{
                              borderTop: '1px solid #E5E7EB',
                              padding: '10px 12px',
                              background: '#F9FAFB',
                            }}
                          >
                            {rec.rationale && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: '#374151',
                                  lineHeight: 1.55,
                                  marginBottom: rec.missing ? 8 : 0,
                                }}
                              >
                                {rec.rationale}
                              </div>
                            )}
                            {rec.missing && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: '#92400E',
                                  marginTop: 4,
                                }}
                              >
                                <span style={{ fontWeight: 600 }}>
                                  Missing:{' '}
                                </span>
                                {rec.missing}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!loading && !messageOnly && !structuredResults && !emptyReason && (
        <div
          style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF' }}
        >
          <div style={{ fontSize: 24, marginBottom: 8 }}>🩺</div>
          <div style={{ fontSize: 13 }}>
            Coding recommendations will appear after Comprehend finishes
          </div>
        </div>
      )}
    </div>
  );
};

export default Coding;
